'use client'

import {stegaClean} from '@sanity/client/stega'
import {useMemo, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
  ProductFacetSidebar,
  type ProductFacetDefinition,
  type ProductFacetOption,
} from '@/components/products/ProductFacetSidebar'
import {ProductCard} from '@/components/products/ProductCard'
import {ProductsGrid} from '@/components/products/ProductsGrid'
import {localizedValue} from '@/components/products/productDisplay'
import {productCanonicalPathForSite} from '@/lib/productCanonical'
import type {CanonicalSettingsDocument, ProductDocument, SectionProductsGrid} from '@/types/sanity'
import type {SiteLite} from '@/multisite/types'

type ProductsGridSectionClientProps = {
  products: ProductDocument[]
  section: SectionProductsGrid
  locale?: string
  site?: SiteLite
  canonicalSettings?: CanonicalSettingsDocument | null
  initialVisibleCount: number
}

type FilterState = {
  brands: string[]
  markets: string[]
  productTypes: string[]
}

type FilterDefinition = {
  key: keyof FilterState
  label: string
  options: ProductFacetOption[]
}

const unique = (values: Array<string | undefined | null>) =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value))))

const productTypeKeyForProduct = (product: ProductDocument) =>
  product.productTypeKey || product.productType?.key || ''

const toggleValue = (values: string[], value: string) =>
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value]

const marketLabel = (value: string, locale?: string, globalLabel?: string) => {
  const normalized = value.toUpperCase()
  if (normalized === 'GLOBAL') return globalLabel || 'Global'

  try {
    const formatter = new Intl.DisplayNames([locale || 'en'], {type: 'region'})
    return formatter.of(normalized) || normalized
  } catch {
    return normalized
  }
}

const matchesFilters = (product: ProductDocument, filters: FilterState) => {
  if (filters.brands.length && !filters.brands.includes(product.brandKey || '')) {
    return false
  }
  if (
    filters.markets.length &&
    !(product.marketKeys || []).some((marketKey) => filters.markets.includes(marketKey))
  ) {
    return false
  }
  if (
    filters.productTypes.length &&
    !filters.productTypes.includes(productTypeKeyForProduct(product))
  ) {
    return false
  }

  return true
}

const buildFacetCounts = (
  products: ProductDocument[],
  options: ProductFacetOption[],
  filters: FilterState,
  key: keyof FilterState,
) => {
  return new Map(
    options.map((option) => [
      option.value,
      products.filter((product) => {
        const matchesOtherFacets =
          (key === 'brands' ||
            !filters.brands.length ||
            filters.brands.includes(product.brandKey || '')) &&
          (key === 'markets' ||
            !filters.markets.length ||
            (product.marketKeys || []).some((marketKey) => filters.markets.includes(marketKey))) &&
          (key === 'productTypes' ||
            !filters.productTypes.length ||
            filters.productTypes.includes(productTypeKeyForProduct(product)))
        if (!matchesOtherFacets) return false

        if (key === 'brands') return product.brandKey === option.value
        if (key === 'markets') return (product.marketKeys || []).includes(option.value)
        return productTypeKeyForProduct(product) === option.value
      }).length,
    ]),
  )
}

export function ProductsGridSectionClient({
  products,
  section,
  locale,
  site,
  canonicalSettings,
  initialVisibleCount,
}: ProductsGridSectionClientProps) {
  const intl = useIntl()
  const [filters, setFilters] = useState<FilterState>({
    brands: [],
    markets: [],
    productTypes: [],
  })
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({})
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount)

  const brandOptions = useMemo<ProductFacetOption[]>(
    () =>
      Array.from(
        new Map(
          products
            .filter((product) => product.brandKey)
            .map((product) => [
              product.brandKey as string,
              product.brand?.title || String(product.brandKey).toUpperCase(),
            ]),
        ).entries(),
      )
        .sort((left, right) => left[1].localeCompare(right[1], locale || 'en'))
        .map(([value, label]) => ({value, label})),
    [locale, products],
  )

  const globalMarketLabel = intl.formatMessage({
    id: 'catalog.marketGlobal',
    defaultMessage: 'Global',
  })
  const marketOptions = useMemo<ProductFacetOption[]>(
    () =>
      unique(products.flatMap((product) => product.marketKeys || []))
        .sort()
        .map((value) => ({value, label: marketLabel(value, locale, globalMarketLabel)})),
    [locale, products, globalMarketLabel],
  )

  const productTypeOptions = useMemo<ProductFacetOption[]>(
    () =>
      unique(products.map((product) => productTypeKeyForProduct(product)))
        .sort()
        .map((value) => {
          const match = products.find((product) => productTypeKeyForProduct(product) === value)
          return {
            value,
            label:
              localizedValue(match?.productType?.labels, locale) ||
              match?.productType?.title ||
              value,
          }
        }),
    [locale, products],
  )

  const visibleProducts = useMemo(
    () => products.filter((product) => matchesFilters(product, filters)),
    [filters, products],
  )
  const pagedProducts = visibleProducts.slice(0, visibleCount)
  const hasMore = visibleProducts.length > pagedProducts.length

  const definitions: ProductFacetDefinition<keyof FilterState>[] = useMemo(() => {
    const hasLockedBrandFilter = Boolean(section.brands?.length || site?.brandKeys?.length)
    const hasLockedMarketFilter = Boolean(section.marketKeys?.length || site?.marketKeys?.length)
    const base: FilterDefinition[] = [
      ...(!hasLockedBrandFilter
        ? [
            {
              key: 'brands' as const,
              label: intl.formatMessage({
                id: 'catalog.filters.brandsLabel',
                defaultMessage: 'Brands',
              }),
              options: brandOptions,
            },
          ]
        : []),
      ...(!hasLockedMarketFilter
        ? [
            {
              key: 'markets' as const,
              label: intl.formatMessage({
                id: 'catalog.filters.marketsLabel',
                defaultMessage: 'Markets',
              }),
              options: marketOptions,
            },
          ]
        : []),
      {
        key: 'productTypes',
        label: intl.formatMessage({
          id: 'catalog.filters.productTypeLabel',
          defaultMessage: 'Product type',
        }),
        options: productTypeOptions,
      },
    ]

    return base.map((definition) => ({
      ...definition,
      values: filters[definition.key],
      counts: buildFacetCounts(products, definition.options, filters, definition.key),
    }))
  }, [
    brandOptions,
    filters,
    intl,
    marketOptions,
    productTypeOptions,
    products,
    section.brands,
    section.marketKeys,
    site?.brandKeys,
    site?.marketKeys,
  ])

  const resolveHref = (product: ProductDocument) =>
    site
      ? productCanonicalPathForSite(product, site, canonicalSettings)
      : `/products/${product.routeSlug || product.productNumber || ''}`

  const clearFilters = () => {
    setFilters({brands: [], markets: [], productTypes: []})
    setVisibleCount(initialVisibleCount)
  }

  const toggleSection = (key: string) => {
    setCollapsedSections((current) => ({
      ...current,
      [key]: !current[key],
    }))
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start">
      <ProductFacetSidebar
        definitions={definitions}
        collapsedSections={collapsedSections}
        onToggleSection={toggleSection}
        onClearAll={clearFilters}
        onToggleOption={(key, value) =>
          setFilters((current) => {
            setVisibleCount(initialVisibleCount)
            return {
              ...current,
              [key]: toggleValue(current[key], value),
            }
          })
        }
      />

      <div>
        <div className="mb-site-md flex items-center justify-between">
          <p className="u-body-sm text-muted-foreground">
            <FormattedMessage
              id="catalog.productCount"
              defaultMessage="{n, plural, =0 {No products} one {# product} other {# products}}"
              values={{n: visibleProducts.length}}
            />
          </p>
        </div>
        {pagedProducts.length ? (
          // stegaClean: section.display arrives stega-wrapped in Presentation mode.
          stegaClean(section.display) === 'carousel' ? (
            <div className="flex snap-x gap-6 overflow-x-auto pb-4">
              {pagedProducts.map((product) => (
                <div key={product._id} className="min-w-[300px] snap-start md:min-w-[360px]">
                  <ProductCard
                    product={product}
                    href={resolveHref(product)}
                    locale={locale}
                    marketKey={site?.marketKeys?.[0]}
                    hideProductPrices={site?.hideProductPrices}
                  />
                </div>
              ))}
            </div>
          ) : (
            <ProductsGrid
              products={pagedProducts}
              locale={locale}
              resolveHref={resolveHref}
              marketKey={site?.marketKeys?.[0]}
              hideProductPrices={site?.hideProductPrices}
            />
          )
        ) : (
          <div className="rounded-[28px] border border-dashed border-neutral-300 bg-white px-8 py-12 text-center shadow-sm">
            <p className="text-lg font-medium text-foreground">
              <FormattedMessage
                id="catalog.noResultsMessage"
                defaultMessage="No products match these filters."
              />
            </p>
          </div>
        )}
        {hasMore ? (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => setVisibleCount((current) => current + initialVisibleCount)}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-neutral-300 bg-white px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition-colors hover:border-neutral-500 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-400"
            >
              <FormattedMessage id="common.loadMore" defaultMessage="Load more" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
